import { ChannelkartNetworkGet, ChannelkartNetworkPost } from "../../../../../services/networkService";
import { getMetaDataConfig } from "../../../../../services/uiConfigService";
import { setMarketLevelTabsList, setOutletLevelTabsList } from "../../../../../stateManagement/actions/manageCouponActions";
import store from "../../../../../stateManagement/store";
import { changeALLTONULL, getEntityPriority, sortHeaderListBasedOnPriority } from "../../../services/services";
import { combinedSelectedValuesType } from "../../../types";
import { ALL, ALL_COLUMNS_OF_MARKET_DETAILS_SLAB_ALL, COLUMN_NAME_MAP_DB_COLUMN_NAME, INVALID_INCLUSION_LIST_RESET, VALID, removeEntites,DB_COLUMN_NAME_MAP_COLUMN_NAME,MetaData } from "../../../utils/utils";
import { applyLocationFilter, showDistributorDesignation } from "./configData";

// Designation cache to store supplier ID -> designations mapping
let designationCache: Record<string, string[]> = {};

/**
 * Enhance supplier name with designation using cache
 * @param supplierValue supplier ID/value
 * @returns enhanced supplier name with designation
 */
export function enhanceSupplierNameWithCache(supplierValue: string): string {
    if (!showDistributorDesignation() || !designationCache[supplierValue]) {
        return supplierValue;
    }
    
    const designations = designationCache[supplierValue];
    if (designations && designations.length > 0) {
        // Map designations: change "supplier" to "distributor" 
        const mappedDesignations = designations.map(designation => 
            designation.toLowerCase() === 'supplier' ? 'distributor' : designation
        );
        const designationText = mappedDesignations.join(', ');
        return `${supplierValue} (${designationText})`;
    }
    
    return supplierValue;
}

/**
 * Clear the designation cache
 */
export function clearDesignationCache(): void {
    designationCache = {};
    console.log('Designation cache cleared');
}


export async function getMarketFilterTabs() :  Promise<string[]> {
    const res = await getMetaDataConfig("schemeIdentifier-1", "location");
    if (!res) throw new Error("no response from meta");
    const data = res?.domainValues?.[0]?.columnNames ?? []
    return data;
}

export async function  getOutletFilterTabs() : Promise<string[]> {
    const res = await getMetaDataConfig("schemeIdentifier-1", "Outlet");
    if (!res) throw new Error("no response from meta");
    const data = res?.domainValues?.[0]?.columnNames ?? [];
    return data.map((columnName : string) => DB_COLUMN_NAME_MAP_COLUMN_NAME[columnName]);
}


/**
 * Builds the URL for getUserDesignationsByLoginIds API
 * @returns API URL string
 */
function getUserDesignationsByLoginIdsFinalUrl(): string {
    return `/users/userDesignationsByLoginIds`;
}

/**
 * Split array into chunks of specified size
 */
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
}

export async function getUserDesignationsByLoginIds(loginIds: string[]): Promise<Record<string, string[]>> {
    try {
        if (!loginIds || loginIds.length === 0) {
            console.log('No loginIds provided');
            return {};
        }
        
        console.log('Fetching designations for suppliers:', loginIds);
        
        // Batch size
        const BATCH_SIZE = 100;
        
        // For small datasets, use single call
        if (loginIds.length <= BATCH_SIZE) {
            const apiUrl = getUserDesignationsByLoginIdsFinalUrl();
            const requestBody = { loginIds };

            const response = await ChannelkartNetworkPost(apiUrl, requestBody);
            
            console.log('API Response:', response);
            return parseDesignationResponse(response);
        }
        
        // For large datasets, use batched approach
        console.log(`Large dataset detected (${loginIds.length} suppliers). Using batched approach with batch size: ${BATCH_SIZE}`);
        
        const batches = chunkArray(loginIds, BATCH_SIZE);
        const allDesignations: Record<string, string[]> = {};
        
        // Process batches sequentially to avoid overwhelming the API
        for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            console.log(`Processing batch ${i + 1}/${batches.length} (${batch.length} suppliers)`);
            
            try {
                const apiUrl = getUserDesignationsByLoginIdsFinalUrl();
                const requestBody = { loginIds: batch };
                const response = await ChannelkartNetworkPost(apiUrl, requestBody);
                
                const batchDesignations = parseDesignationResponse(response);
                
                // Merge batch results
                Object.assign(allDesignations, batchDesignations);
                
                // Add small delay between batches to be API-friendly
                if (i < batches.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            } catch (error) {
                console.error(`Error processing batch ${i + 1}:`, error);
                // Continue with next batch instead of failing completely
            }
        }
        
        console.log('Combined designation map:', allDesignations);
        return allDesignations;
        
    } catch (error) {
        console.error('Error fetching user designations:', error);
        console.error('Error details:', error);
        return {};
    }
}

/**
 * Parse API response into designation map
 */
function parseDesignationResponse(response: any): Record<string, string[]> {
    const designationMap: Record<string, string[]> = {};
    
    let dataArray = response;
    if (response && response.data) {
        dataArray = response.data;
    }
    
    if (Array.isArray(dataArray)) {
        dataArray.forEach((item: { loginId: string, designation: string[] }) => {
            if (item.loginId && item.designation) {
                designationMap[item.loginId] = item.designation || [];
            }
        });
    }
    
    return designationMap;
}

/**
 * get options for market and outlet filters
 * @param currentSelectedValues 
 * @param currTabPriority 
 * @returns 
 */

export const getMarketDetailsOptions=async(currentSelectedValues:combinedSelectedValuesType,currTabPriority:number , currentTabName:string,searchText?:string,name?:string)=>{
    let res;
    if(isAnyFieldSelected(currentSelectedValues) || currentTabName.toLowerCase() === "outletcode" || currentTabName.toLowerCase() === "distributordetails" || currentTabName.toLowerCase() === "asm"){
        const requestBody = getFiltersForMarketandOutletDetailsAPI(currentSelectedValues,currTabPriority,currentTabName,searchText);
        res = await getMarketandOutletDetails(requestBody);
    }else{
        res = await getMarketOrOutletDetail(currentTabName,name);
    }
  
    const finalRes:Record<string,{label:string,value:string}[]> = {};

    for (let prop in res) {
        finalRes[prop] = res[prop].map((item: string) => {
            return {
                label: item,
                value: item,
            }
        })
    }

    if (currentTabName.toLowerCase() === "distributordetails" && showDistributorDesignation()) {

        const supplierKey = "supplier";
        
        if (finalRes[supplierKey] && finalRes[supplierKey].length > 0) {
            try {
                console.log('Processing distributorDetails with supplier data');
                console.log('Raw supplier options:', finalRes[supplierKey]);
                
                const supplierIds = finalRes[supplierKey].map(option => option.value);
                
                // Fetch designations for these suppliers
                const designationMap = await getUserDesignationsByLoginIds(supplierIds);
                
                // Cache the designations for future use
                Object.assign(designationCache, designationMap);
                console.log('Updated designation cache:', designationCache);
                
                // Only enhance the supplier property, not all properties
                finalRes[supplierKey] = finalRes[supplierKey].map(option => {
                    const designations = designationMap[option.value];
                    if (designations && designations.length > 0) {
                        const mappedDesignations = designations.map(designation => 
                            designation.toLowerCase() === 'supplier' ? 'distributor' : designation
                        );
                        // Format: supplier1 (designation1)
                        const designationText = mappedDesignations.join(', ');
                        const enhancedLabel = `${option.label} (${designationText})`;
                        return {
                            ...option,
                            label: enhancedLabel
                        };
                    }
                    return option;
                });
            } catch (error) {
                console.error('Error enhancing supplier options with designations:', error);
                console.error('Error details:', error);
                // Continue with original options if enhancement fails
            }
        } else {
            console.log('No supplier data found in API response or supplier array is empty');
        }
    }

    return finalRes;
}

/**
 * This function return filters for the market and outlet details api
 * @param currentSelectedValues 
 * @param currTabPriority 
 * @returns 
 */

const getFiltersForMarketandOutletDetailsAPI = (currentSelectedValues: combinedSelectedValuesType, currTabPriority: number, currentTabName: string, searchText?: string) => {
    const filter: Record<string, any> = {
        "location": getLocationFilter(currentSelectedValues.schemeLocationBifurcations, currTabPriority),
        "outlet": getOutletFilter(currentSelectedValues.schemeOutletBifurcations, currTabPriority),
        "supplier": getSupplierFilter(currentSelectedValues.schemeOutletBifurcations, currTabPriority),

    }

    if (currentTabName === "outletCode") {
        filter.regEx = {
            "outletcodes": [
                currentTabName === "outletCode" ? searchText : ""
            ]
        }
    }

    return filter;
}

/**
 * It return the location filter for market and outlet details api
 * @param values 
 * @param currTabPriority 
 * @returns 
 */

const getLocationFilter = (values: Record<string, string[]>, currTabPriority: number) => {
    const ans: Record<string, string[]> = {};
    // exclude id and all the colums which have lower priority (higher numeric value) than current tab

    for (let prop in values) {
        if (prop !== 'id' && getEntityPriority(prop) < currTabPriority && values[prop] && values[prop].length > 0)
            ans[COLUMN_NAME_MAP_DB_COLUMN_NAME[prop]] = values[prop]
    }

    // // sort the headersList data based on reverse priority so that it matches with location heirarchy
    const reversedObject: Record<string, string[]> = {};
    const keys = Object.keys(ans);
    const sortedHeaderList = sortHeaderListBasedOnPriority(keys).reverse();

    for (let i = 0; i < sortedHeaderList.length; i++) {
        reversedObject[sortedHeaderList[i]] = ans[sortedHeaderList[i]]
    }
    return reversedObject;
}

/**
 * It return the supplier filters for market and outelt detials api
 * @param values 
 * @param currTabPriority 
 * @returns 
 */

const getSupplierFilter = (values: Record<string, string[]>, currTabPriority: number) => {
    // exclude all the colums which have lower priority (higher numeric value) than current tab

    if (getEntityPriority('distributorDetails') >= currTabPriority) return {}
    if (!values["distributorDetails"] || values["distributorDetails"].length === 0) return {}

    return {
        "value": values["distributorDetails"]
    }
}

/**
 * This function returns outlet filters for market and outlet details api
 * @param values 
 * @param currTabPriority 
 * @returns 
 */
const getOutletFilter = (values: Record<string, string[]>, currTabPriority: number) => {
    const ans: Record<string, string[]> = {};
    // excluse outletCode , id and all the colums which have lower priority (higher numeric value) than current tab
    for(let prop in values){
        if(prop !== 'outletCode' && prop !=='id' && prop !=="distributorDetails" && values[prop] && values[prop].length > 0 && getEntityPriority(prop)<currTabPriority){// less numeric value means high priority
            ans[COLUMN_NAME_MAP_DB_COLUMN_NAME[prop]] = values[prop].map((item)=>getValueFromValueConcatenatedLabel(item,prop));
        } 
            
    }

    return ans;
}

/**
 * 
 */
export function getASMFromASMConcatString(data: string):string{
    return data.split(" (")[0];
}

export const getValueFromValueConcatenatedLabel=(label:string,entityName:string):string=>{
    if(entityName === 'asm') return getASMFromASMConcatString(label);
    else return label
}

/**
 * This function creates the final url with filters
 * @param entityName 
 * @param columnName 
 * @param searchText 
 * @param filterText 
 * @returns 
 */
function getDistictFieldDataFinalUrl(entityName: string, columnName: string, searchText: string = "", filterText?: string) {
    let filterApplied = false;
    let url = `/v1/distinctFieldData/${entityName}/${columnName}`;
    if (searchText && searchText !== "") {
        url += `?filter=${columnName}[regex]:${searchText}`
        filterApplied = true;
    }
    if (filterText && filterText !== "") {
        url += filterApplied ? `&${filterText}` : `?filter=${filterText}`
        filterApplied = true;
    }
    url+=filterApplied?'&size=1000':'?size=1000';
    return url;
}

/**
 * 
 * @returns url to get market and outlet details
 */

function getMarketandOutletDetailsFinalUrl() {
    return "/v1/getMarketsAndOutletDetails";
}

/**
 * returns market and outlet details data
 * @param requestBody 
 * @returns 
 */
export async function getMarketandOutletDetails(requestBody: object): Promise<Record<string, any[]>> {
    try {
        const finalUrl = getMarketandOutletDetailsFinalUrl();
        const data = await ChannelkartNetworkPost(finalUrl, requestBody);
        return data.data ?? [];

    } catch (error) {
        console.error(error);
        return {};
    }
}

/**
 * returns market and outlet details data
 * @param requestBody 
 * @returns 
 */
export async function getMarketOrOutletDetail(currentTabName:string,name?:string):Promise<Record<string,any[]>>{
    try {
        let entityName = "";
        if(name === "outlet"){
            entityName = "OutletDetails";
        }else{
            entityName = "Location"
        }
        const data = getUniqueValuesFromDistinctField(entityName,currentTabName);
        return data;
    } catch (error) {
        console.error(error);
        return {};
    }
}

/**
 * Get values from distinct field api and convert it into (label , value) format
 * so that we can send it to CustomSelectWithSearch component
 * @param entityName 
 * @param columnName 
 * @param searchText
 * @param filterText 
 * @param requestBody 
 * @returns 
 */


export async function getUniqueValuesFromDistinctField(entityName: string, columnName: string,searchText?:string, filterText?:any ,requestBody?:object): Promise<Record<string,string[]>> {
    try {
        columnName = COLUMN_NAME_MAP_DB_COLUMN_NAME[columnName]?? columnName;
        const finalUrl = getDistictFieldDataFinalUrl(entityName,columnName,searchText,filterText);
        const finalData = await ChannelkartNetworkGet(finalUrl,requestBody);
        const finalValues: string[] = [];
        finalData?.data?.forEach((value: any) => {
            if (value[columnName] != null || value[columnName] !== "" || value[columnName] !== undefined) {
                finalValues.push(value[columnName]);
            }
        })
        
        return {[columnName] :finalValues};

    } catch (error) {
        console.error(error);
        return {};
    }
}

/**
 * This funcion generated rows for market details table
 * @param tabsList 
 * @param schemeLocationBifurcationsList 
 * @param schemeOutletBifurcationsList 
 * @returns 
 */

export const generateRowsMarketDetails = (tabsList: string[], schemeLocationBifurcationsList: any[], schemeOutletBifurcationsList: any[]) => {

    const dataList = getMergedDataWithSameIdInTwoArrays(schemeLocationBifurcationsList, schemeOutletBifurcationsList);
    const rows: Array<Array<string>> = dataList.map((item: Record<string, any>) => {
        const row: Array<string> = [];
        tabsList.forEach((header: string) => {
            const selectedValues = item?.[header];
            if (selectedValues && selectedValues?.length === 0) row.push(ALL);
            else if (selectedValues && Array.isArray(selectedValues)) {
                // Enhance distributor details with designations from cache
                const enhancedValues = header.toLowerCase() === "distributordetails" 
                ? selectedValues.map(val => enhanceSupplierNameWithCache(val)): selectedValues;
                
                let str = enhancedValues[0];
                enhancedValues.slice(1).forEach((val: string) => {
                    str += '*break*' + val;
                })
                row.push(str);
            }
        })
        return row;
    })
    return rows;
}

/**
 * This function return the combined data in two array which have same id
 * @param arr1 array 1
 * @param arr2 array 2
 * @returns array with combined data
 */

const getMergedDataWithSameIdInTwoArrays = (arr1: any[], arr2: any[]) => {
    const obj: Record<string, any> = {};

    arr1.forEach((item: { id: string }) => {
        obj[item.id] = { ...item };
    })

    arr2.forEach((item: { id: string }) => {
        obj[item.id] = { ...obj[item.id], ...item }
    })

    return Object.values(obj);

}

/**
 * get the row data from market details (location + outlet) and convert it in the form we store in state
 * used when we edit any row
 * @param index 
 * @returns 
 */
export function getMarketDetailsRowDataInFormOfState(index: number): combinedSelectedValuesType {
    const { schemeLocationBifurcationsList, schemeOutletBifurcationsList } = store.getState()?.manageCoupons?.data;
    const schemeOutletBifurcationsListData: Record<string, any> = schemeOutletBifurcationsList[index];
    const schemeLocationBifurcationsListData: Record<string, any> = schemeLocationBifurcationsList[index];
    return {
        schemeLocationBifurcations: changeALLTONULL(schemeLocationBifurcationsListData),
        schemeOutletBifurcations: changeALLTONULL(schemeOutletBifurcationsListData)
    }
}



/**
 * It returns the combined header list of both the market filters and outlet filters
 * @returns 
 */

export function getCombinedTabListOfMarketDetails() {
    const { marketLevelTabsList, outletLevelTabsList } = store.getState()?.manageCoupons?.configData;
    if (marketLevelTabsList && outletLevelTabsList) return [...marketLevelTabsList, ...outletLevelTabsList];
    if (marketLevelTabsList) return marketLevelTabsList;
    if (outletLevelTabsList) return outletLevelTabsList;
    return [];
}



/**
 * It return the market tabs Header list
 * if it present in redux the it return that list else it makes an api call to get the headersList
 * @returns 
 */
export async function getMarketTabsList(){
    const {marketLevelTabsList} = store.getState()?.manageCoupons?.configData;
    
    try {
        if (!marketLevelTabsList || marketLevelTabsList.length === 0) {
            const list = await getMarketFilterTabs();
            const filteredHeaderList = list.filter((header:string) => MetaData.removeEntites.indexOf(header) < 0);
            const sorted = sortHeaderListBasedOnPriority(filteredHeaderList);
            setMarketLevelTabsList(sorted);
            return sorted;
        } 
        else return marketLevelTabsList
    } catch (error) {
        console.error(error);
        return [];
    }
   
}

/**
 * It return the outlet tabs Header list
 * if it present in redux the it return that list else it makes an api call to get the headersList
 * @returns 
 */

export async function getOutletTabsList() {
    try {
        const { outletLevelTabsList } = store.getState()?.manageCoupons?.configData;
        if (!outletLevelTabsList || outletLevelTabsList.length === 0) {
            const list = await getOutletFilterTabs();
            const filteredHeaderList = list.filter((header: string) =>MetaData.removeEntites.indexOf(header) < 0);
            const sorted = sortHeaderListBasedOnPriority(filteredHeaderList);
            setOutletLevelTabsList(sorted);
            return sorted;
        }
        else return outletLevelTabsList
    } catch (error) {
        console.error(error);
        return [];
    }
}

/**
 * This function return the list of tabNames which require multi select options
 * @returns 
 */
export function getMultiSelectTabNames() {
    return ["outletCode"];
}

/**
 * This function creates the filters for get outletCode api
 * @param values 
 * @returns string
 */

export const getOutletFilterText = async (values: Record<string, string[]>): Promise<string> => {
    const headerList = await getOutletFilterTabs();
    let str = '';
    headerList.forEach((header: string) => {
        if (values[header] && values[header]?.length > 0 && header !== 'distributorDetails' && header !== 'outletCode') {
            const dbColName = COLUMN_NAME_MAP_DB_COLUMN_NAME[header];
            // str+=`${dbColName}[in]:[${values[header]}]&`;
            str += `${dbColName}[in]:[${values[header].map((val) => encodeURIComponent(val.replace(/\band\b|\bAND\b/gi, "And")))}]&`
        }
    })
    // remove & at the end
    return str === '' ? str : str.slice(0, -1);
}

export const filterOuletCodeFromInclusionList = (inclusionList: string[]) => {
    const overlappingData: string[] = [];
    const nonOverlappingData: string[] = [];

    let marketSelectorOutletCodes: string[] = [];
    const { schemeOutletBifurcationsList } = store.getState()?.manageCoupons?.data;

    schemeOutletBifurcationsList.forEach((grp: any) => {
        marketSelectorOutletCodes = [...marketSelectorOutletCodes, ...grp.outletCode]
    })

    inclusionList.forEach((outletCode: string) => {
        if (marketSelectorOutletCodes.includes(outletCode)) {
            overlappingData.push(outletCode);
        } else {
            nonOverlappingData.push(outletCode);
        }
    })



    return { overlappingData, nonOverlappingData };
}

export const validateXLSXFileType = (file: File) => {
    return new Promise((resolve, reject) => {
        const reader: any = new FileReader();

        reader.onloadend = function () {
            const fileArray: Uint8Array = new Uint8Array(reader.result);

            // XLSX files start with the magic number: 50 4B 03 04
            const xlsxMagicNumber: Uint8Array = new Uint8Array([80, 75, 3, 4]);

            // Check if the read bytes match the expected magic number
            if (arrayEquals(fileArray.slice(0, xlsxMagicNumber.length), xlsxMagicNumber)) {
                resolve(true);
            } else {
                resolve(false);
            }
        };

        reader.onerror = function () {
            reject(new Error("Error reading the file."));
        };

        // Read the first few bytes of the file
        const blob = file.slice(0, 4);
        reader.readAsArrayBuffer(blob);
    });
};

function arrayEquals(arr1: any, arr2: any) {
    return arr1.every((value: any, index: number) => value === arr2[index]);
}


/**
 * return true if all is present in any row in market details table
 * @returns boolean 
 */
export async  function isInclusionDisable(){
    return await ALLDataALLInMarketDetails();
}

export async function ALLDataALLInMarketDetails() {
    const { schemeOutletBifurcationsList, schemeLocationBifurcationsList } = store.getState()?.manageCoupons?.data;

    const list = getMergedDataWithSameIdInTwoArrays(schemeLocationBifurcationsList , schemeOutletBifurcationsList);

    return await ALLDataAllCheckInMarketDetails(list);
}

export async function ALLDataAllCheckInMarketDetails(list:any[]){
    let locationHeaderList:string[]=[]
    if(applyLocationFilter()){
     locationHeaderList = await getMarketTabsList();
    }
    const outletHeadersList:string[] = await getOutletTabsList();
    
    for(let i=0 ; i<list.length ; i++){ 
        const obj = list[i];
        let allDataAll= true;
        if(applyLocationFilter()){
            locationHeaderList.forEach((header) => {
                if(obj[header] && obj[header].length > 0) allDataAll = false;
            });
        }      

        outletHeadersList.forEach((header) => {
            if(obj[header] && obj[header].length > 0) allDataAll = false;
        });

        if(allDataAll) return true;
    }

  }


 export const isMarketFilterEmpty=()=>{
    return isLocationFilterEmpty() || isOutletFilterEmpty();
 }


const isLocationFilterEmpty = () => {
    const { schemeLocationBifurcationsList } = store.getState()?.manageCoupons?.data;
    return schemeLocationBifurcationsList.length === 0;
}

const isOutletFilterEmpty = () => {
    const { schemeOutletBifurcationsList } = store.getState()?.manageCoupons?.data;
    return schemeOutletBifurcationsList.length === 0;
}



export const isInclusionListEmpty=()=>{
    const { list } = store.getState()?.manageCoupons?.data?.inclusionData;
    return list.length === 0;
}

export const isExclusionListEmpty=()=>{
    const { list } = store.getState()?.manageCoupons?.data?.exclusionData;
    return list.length === 0;
}

export async function validateCurrentMarketSelection(values:combinedSelectedValuesType){
    const {schemeLocationBifurcations , schemeOutletBifurcations} = values;
    const { inclusionData , exclusionData } = store.getState()?.manageCoupons?.data;

    const list = [{...schemeLocationBifurcations , ...schemeOutletBifurcations}];
    
    const allDataAllRes =  await ALLDataAllCheckInMarketDetails(list);

    if( allDataAllRes && (inclusionData.list.length > 0 || exclusionData.list.length > 0)){
        return INVALID_INCLUSION_LIST_RESET;
    }

    if( allDataAllRes ){
        return ALL_COLUMNS_OF_MARKET_DETAILS_SLAB_ALL;
    }
    return VALID;
}

export function isInclusionEmpty(){
    const { inclusionData } = store.getState()?.manageCoupons?.data;
    return inclusionData.list.length === 0 && inclusionData.file === null;
}

export function isExclusionEmpty(){
    const { exclusionData } = store.getState()?.manageCoupons?.data;
    return exclusionData.list.length === 0 && exclusionData.file === null;
}

//check if any filter in market or location is selected.
function isAnyFieldSelected(currentSelectedValues: combinedSelectedValuesType) {
    let flag = false;

    Object.values(currentSelectedValues.schemeLocationBifurcations).forEach( items => {
        if(items.length > 0){
            flag = true;
        }
    })

    Object.values(currentSelectedValues.schemeOutletBifurcations).forEach( items => {
        if(items.length > 0){
            flag = true;
        }
    })
    
    return flag;
}

export async function uploadInclusionFile(inclusionFile: File) {
    const requestFormData = new FormData();
    requestFormData.append('file', inclusionFile);
    requestFormData.append('featureId', 'mdm');
    
    try {
        const response = await ChannelkartNetworkPost("/v1/uploadFile/mdmuploads", requestFormData, {"Content-Type": "multipart/form-data"});
        if(response.status !== 200 && response.status !== 201) {
            throw new Error("Some error occured while uploading inclusion list");
        }
        return response.data.features[0].Uri;
    } catch (error) {
        throw error;
    }
}

export async function uploadExclusionFile(exclusionFile: File) {
    const requestFormData = new FormData();
    requestFormData.append('file', exclusionFile);
    requestFormData.append('featureId', 'mdm');
    
    try {
        const response = await ChannelkartNetworkPost("/v1/uploadFile/mdmuploads", requestFormData, {"Content-Type": "multipart/form-data"});
        if(response.status !== 200 && response.status !== 201) {
            throw new Error("Some error occured while uploading exclusion list");
        }
        return response.data.features[0].Uri;
    } catch (error) {
        throw error;
    }
}
